import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

/** Canonical facility options for the UI */
const FACILITY_OPTIONS = [
  { value: "whatsapp",       label: "WhatsApp" },
  { value: "payments",       label: "Payments" },
  { value: "internal_chats", label: "Internal Chats" },
  { value: "chatbots",       label: "Chatbots" },
  { value: "ai_scenarios",   label: "AI Scenarios" },
];

const normalizeFacility = (s) => {
  if (!s) return "";
  let v = String(s).trim().toLowerCase();
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    v = v.slice(1, -1).trim();
  }
  v = v.replace(/\s+/g, "_");
  if (v === "internal_chat") v = "internal_chats";
  if (v === "ai_senerious" || v === "ai_scenarious" || v === "ai_scenario") v = "ai_scenarios";
  return FACILITY_OPTIONS.some(o => o.value === v) ? v : "";
};

const parseFacilities = (raw) => {
  if (Array.isArray(raw)) return raw.map(normalizeFacility).filter(Boolean);
  if (raw == null) return [];
  const s = String(raw).trim();

  if (s.startsWith("[")) {
    try {
      const arr = JSON.parse(s);
      return Array.isArray(arr) ? arr.map(normalizeFacility).filter(Boolean) : [];
    } catch {/* ignore */}
  }
  if (s.startsWith("{") && s.endsWith("}")) {
    const quoted = [...s.matchAll(/"([^"]*)"/g)].map(m => m[1]);
    if (quoted.length) return quoted.map(normalizeFacility).filter(Boolean);
    const inner = s.slice(1, -1);
    if (!inner) return [];
    return inner.split(",").map(normalizeFacility).filter(Boolean);
  }
  return s.split(",").map(normalizeFacility).filter(Boolean);
};

const PackagesEdit = () => {
  const navigate = useNavigate();
  const { data: id } = useParams();

  // helpers
  const durationToDays = { monthly: 30, quarterly: 90, "semi-quarterly": 180, yearly: 365 };
  const validDurations = Object.keys(durationToDays);

  // form state
  const [name, setName] = useState("");
  const [type, setType] = useState("package");
  const [workspaceMode, setWorkspaceMode] = useState("unlimited");
  const [workspaceLimit, setWorkspaceLimit] = useState("");
  const [users, setUsers] = useState("");
  const [application_users, setApplicationUsers] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [priceAmount, setPriceAmount] = useState("");
  const [priceCurrency, setPriceCurrency] = useState("USD");
  const [duration, setDuration] = useState("monthly");

  // meta
  const [rowData, setRowData] = useState({ id: null });
  const [loading, setLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(false);

  // errors
  const [nameErr, setNameErr] = useState("");
  const [typeErr, setTypeErr] = useState("");
  const [workspaceModeErr, setWorkspaceModeErr] = useState("");
  const [workspaceLimitErr, setWorkspaceLimitErr] = useState("");
  const [usersErr, setUsersErr] = useState("");
  const [appUsersErr, setAppUsersErr] = useState("");
  const [facilitiesErr, setFacilitiesErr] = useState("");
  const [priceAmountErr, setPriceAmountErr] = useState("");
  const [priceCurrencyErr, setPriceCurrencyErr] = useState("");
  const [durationErr, setDurationErr] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const clearAllErrors = () => {
    setNameErr(""); setTypeErr(""); setWorkspaceModeErr(""); setWorkspaceLimitErr("");
    setUsersErr(""); setAppUsersErr(""); setFacilitiesErr("");
    setPriceAmountErr(""); setPriceCurrencyErr(""); setDurationErr("");
    setErrorMessage("");
  };

  const onFacilitiesChange = (e) => {
    const vals = Array.from(e.target.selectedOptions).map(o => o.value);
    setFacilities(vals);
  };

  // load existing package
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/packages/${id}`, {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        authorization:
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImFkbWluQGF1dGhmYXN0LmNvbSIsImlkIjo1LCJ1c2VyVHlwZSI6InN1cGVyYWRtaW4iLCJpYXQiOjE2NjM2NTE2NzV9.raQf8JTVyiM51zg6OgH1RLkQqK_QyGPO2rJa9-oYDko",
      }),
    })
      .then(r => r.json())
      .then(resp => {
        const data = resp?.data || {};
        setRowData(data);

        setName(data.name || "");
        setType(data.type || "package");

        const mode = (data.workspace_mode === "limited" || data.workspace_mode === "unlimited")
          ? data.workspace_mode
          : "unlimited";
        setWorkspaceMode(mode);
        setWorkspaceLimit(mode === "limited"
          ? (data.workspace_limit === 0 || data.workspace_limit) ? String(data.workspace_limit) : ""
          : ""
        );

        setUsers((data.users === 0 || data.users) ? String(data.users) : "");
        setApplicationUsers(
          (data.application_users === 0 || data.application_users) ? String(data.application_users) : ""
        );

        setFacilities(parseFacilities(data.facilities));

        setPriceAmount((data.price_amount === 0 || data.price_amount) ? String(data.price_amount) : "");
        setPriceCurrency(data.price_currency || "USD");

        const dLabel = validDurations.includes(data.duration_label) ? data.duration_label : "monthly";
        setDuration(dLabel);
      })
      .catch(() => setErrorMessage("Failed to load package."))
      .finally(() => setLoading(false));
  }, [id]);

  const basicValidate = () => {
    let ok = true;
    clearAllErrors();

    if (!name.trim()) { setNameErr("Name is required."); ok = false; }
    if (!type) { setTypeErr("Type is required."); ok = false; }
    if (!["limited", "unlimited"].includes(workspaceMode)) {
      setWorkspaceModeErr("Choose limited or unlimited."); ok = false;
    }
    if (workspaceMode === "limited") {
      const n = Number(workspaceLimit);
      if (!workspaceLimit || Number.isNaN(n) || n <= 0) {
        setWorkspaceLimitErr("Enter a valid number of workspaces."); ok = false;
      }
    }
    if (users !== "" && Number(users) < 0) { setUsersErr("Users cannot be negative."); ok = false; }
    if (application_users !== "" && Number(application_users) < 0) {
      setAppUsersErr("Application users cannot be negative."); ok = false;
    }
    const amt = Number(priceAmount);
    if (priceAmount === "" || Number.isNaN(amt) || amt < 0) {
      setPriceAmountErr("Enter a valid non-negative price."); ok = false;
    }
    if (!priceCurrency) { setPriceCurrencyErr("Select a currency."); ok = false; }
    if (!validDurations.includes(duration)) { setDurationErr("Select a valid duration."); ok = false; }

    return ok;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!rowData?.id) { setErrorMessage("Package id not found."); return; }
    if (!basicValidate()) return;

    try {
      setShowLoading(true);

      // Keep nested shape (workspaces + price)
      const payload = {
        name,
        type,
        workspaces: {
          mode: workspaceMode,
          limit: workspaceMode === "limited" ? Number(workspaceLimit) : null,
        },
        users: users === "" ? 0 : Number(users),
        application_users: application_users === "" ? 0 : Number(application_users),
        facilities,
        price: { amount: Number(priceAmount), currency: priceCurrency },
        duration,
        duration_in_days: durationToDays[duration] || 30,
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/packages/update/${rowData.id}`,
        {
          method: "PATCH",
          headers: new Headers({
            "Content-Type": "application/json",
            authorization:
              "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImFkbWluQGF1dGhmYXN0LmNvbSIsImlkIjo1LCJ1c2VyVHlwZSI6InN1cGVyYWRtaW4iLCJpYXQiOjE2NjM2NTE2NzV9.raQf8JTVyiM51zg6OgH1RLkQqK_QyGPO2rJa9-oYDko",
          }),
          body: JSON.stringify(payload),
        }
      );

      const responseData = await res.json().catch(() => ({}));
      setShowLoading(false);

      if (res.status === 200 || res.status === 201) {
        navigate("/packages");
      } else if (res.status === 400) {
        setErrorMessage(responseData.message || "Bad request.");
      } else if (res.status === 422 && Array.isArray(responseData.errors)) {
        const getMsg = (prop) => {
          const found = responseData.errors.find((x) => x.property === prop);
          if (found?.constraints) {
            const vals = Object.values(found.constraints);
            return vals?.length ? vals[0] : "";
          }
          return "";
        };
        setNameErr(getMsg("name"));
        setTypeErr(getMsg("type"));
        setWorkspaceModeErr(getMsg("workspaces.mode"));
        setWorkspaceLimitErr(getMsg("workspaces.limit"));
        setUsersErr(getMsg("users"));
        setAppUsersErr(getMsg("application_users"));
        setFacilitiesErr(getMsg("facilities"));
        setPriceAmountErr(getMsg("price.amount") || getMsg("priceAmount"));
        setPriceCurrencyErr(getMsg("price.currency") || getMsg("priceCurrency"));
        setDurationErr(getMsg("duration") || getMsg("duration_in_days"));
      } else {
        setErrorMessage(responseData.message || "Failed to update package.");
      }
    } catch (err) {
      setShowLoading(false);
      setErrorMessage("Network error. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar: Back on left, centered title */}
      <div className="w-full bg-blue-600 text-white">
        <div className="relative max-w-4xl mx-auto px-4 py-3 flex items-center justify-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="absolute left-3 inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm font-medium"
          >
            ← Back
          </button>
          <h1 className="text-lg md:text-xl font-semibold">
            Update Package Details
          </h1>
        </div>
      </div>

      {/* Card */}
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="mx-auto w-full max-w-lg rounded-2xl bg-white shadow-md ring-1 ring-black/5">
          {loading ? (
            <div className="px-6 py-10 text-center text-gray-600">Loading…</div>
          ) : (
            <form onSubmit={handleUpdate} className="px-6 py-6 space-y-4">
              {/* Name */}
              <div>
                <label className="label"><span className="label-text">Name</span></label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="Enter Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <p className="text-[#ff3333] text-sm mt-1">{nameErr}</p>
              </div>

              {/* Type */}
              <div>
                <label className="label"><span className="label-text">Type</span></label>
                <select
                  className="select select-bordered w-full"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  <option value="package">Package</option>
                  <option value="add on">Add On</option>
                </select>
                <p className="text-[#ff3333] text-sm mt-1">{typeErr}</p>
              </div>

              {/* Workspaces */}
              <div>
                <label className="label"><span className="label-text">Workspaces</span></label>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="workspaceMode"
                      className="radio"
                      checked={workspaceMode === "unlimited"}
                      onChange={() => setWorkspaceMode("unlimited")}
                    />
                    <span>Unlimited</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="workspaceMode"
                      className="radio"
                      checked={workspaceMode === "limited"}
                      onChange={() => setWorkspaceMode("limited")}
                    />
                    <span>Limited</span>
                  </label>
                </div>
                <p className="text-[#ff3333] text-sm mt-1">{workspaceModeErr}</p>

                {workspaceMode === "limited" && (
                  <div className="mt-3">
                    <input
                      type="number"
                      min={1}
                      className="input input-bordered w-full"
                      placeholder="Number of workspaces"
                      value={workspaceLimit}
                      onChange={(e) => setWorkspaceLimit(e.target.value)}
                    />
                    <p className="text-[#ff3333] text-sm mt-1">{workspaceLimitErr}</p>
                  </div>
                )}
              </div>

              {/* Users / App users */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label"><span className="label-text">Users</span></label>
                  <input
                    type="number"
                    min={0}
                    className="input input-bordered w-full"
                    placeholder="Total users"
                    value={users}
                    onChange={(e) => setUsers(e.target.value)}
                  />
                  <p className="text-[#ff3333] text-sm mt-1">{usersErr}</p>
                </div>
                <div>
                  <label className="label"><span className="label-text">Application Users</span></label>
                  <input
                    type="number"
                    min={0}
                    className="input input-bordered w-full"
                    placeholder="Application users"
                    value={application_users}
                    onChange={(e) => setApplicationUsers(e.target.value)}
                  />
                  <p className="text-[#ff3333] text-sm mt-1">{appUsersErr}</p>
                </div>
              </div>

              {/* Facilities */}
              <div>
                <label className="label"><span className="label-text">Facilities</span></label>
                <select
                  multiple
                  className="select select-bordered w-full"
                  value={facilities}
                  onChange={onFacilitiesChange}
                >
                  {FACILITY_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="text-[#ff3333] text-sm mt-1">{facilitiesErr}</p>
              </div>

              {/* Price */}
              <div>
                <label className="label"><span className="label-text">Price</span></label>
                <div className="flex gap-3">
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    className="input input-bordered w-full"
                    placeholder="Amount"
                    value={priceAmount}
                    onChange={(e) => setPriceAmount(e.target.value)}
                  />
                  <select
                    className="select select-bordered"
                    value={priceCurrency}
                    onChange={(e) => setPriceCurrency(e.target.value)}
                  >
                    <option value="USD">USD ($)</option>
                    <option value="INR">INR (₹)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="AED">AED (د.إ)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
                <p className="text-[#ff3333] text-sm mt-1">
                  {priceAmountErr || priceCurrencyErr}
                </p>
              </div>

              {/* Duration */}
              <div>
                <label className="label"><span className="label-text">Package Duration</span></label>
                <select
                  className="select select-bordered w-full"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                >
                  <option value="monthly">Monthly (30 days)</option>
                  <option value="quarterly">Quarterly (90 days)</option>
                  <option value="semi-quarterly">Semi-Quarterly (180 days)</option>
                  <option value="yearly">Yearly (365 days)</option>
                </select>
                <p className="text-[#ff3333] text-sm mt-1">{durationErr}</p>
              </div>

              {/* server msg */}
              {errorMessage ? (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {errorMessage}
                </div>
              ) : null}

              {/* <div className="pt-2">
                <button type="submit" className={`btn btn-primary ${showLoading ? "btn-disabled" : ""}`} disabled={showLoading}>
                  {showLoading ? "Updating package..." : "Update"}
                </button>
              </div> */}

               <div className="pt-2 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={showLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                // className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-white ${
                //   showLoading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                // }`}
                  className="inline-flex items-center rounded-md border border-blue-300 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-700"
                disabled={showLoading}
              >
                {showLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg
                      className="h-4 w-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Updating…
                  </span>
                ) : (
                  "Update"
                )}
              </button>
            </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackagesEdit;
